'use client'

import { useEffect, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useAuthStore } from '@/lib/stores/auth-store'
import { useThemeStore } from '@/lib/stores/theme-store'
import { getWhiteLabelDomain } from '@/lib/utils'
import { BlockedMemberArea } from '@/components/flix/blocked-member-area'

import { Home, BookOpen, Trophy, User, LogOut, Settings, Menu } from 'lucide-react'

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/dashboard', icon: Home },
  { title: 'Trilhas', href: '/trilhas', icon: Trophy },
  { title: 'Cursos', href: '/cursos', icon: BookOpen },
  { title: 'Perfil', href: '/perfil', icon: User },
]

const MEMBER_AREA_PATHS = [
  '/agenda',
  '/agendar',
  '/bem-vindo',
  '/curso-online',
  '/kanban',
  '/kanban-vertical',
  '/mensagens',
  '/mentoria-continuada',
  '/perfil',
  '/playground',
  '/relatorio',
]

function isMemberAreaPath(pathname: string) {
  return MEMBER_AREA_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

function MemberAreaVerificationState() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4" style={{ backgroundColor: 'var(--body-bg, #0a0a0a)' }}>
      <div className="text-center text-white">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-neutral-700 border-t-orange-500" />
        <p className="text-sm text-neutral-300">Verificando disponibilidade da área de membros...</p>
      </div>
    </div>
  )
}

function MemberAreaErrorState({ notFound, onRetry }: { notFound: boolean; onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4" style={{ backgroundColor: 'var(--body-bg, #0a0a0a)' }}>
      <div className="max-w-sm text-center text-white">
        <p className="text-base font-semibold">
          {notFound ? 'Área não encontrada' : 'Não foi possível carregar a área de membros'}
        </p>
        <p className="mt-2 text-sm text-neutral-400">
          {notFound
            ? 'Confira se o endereço está correto ou fale com quem enviou o link.'
            : 'Verifique sua conexão e tente de novo.'}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
        >
          Tentar de novo
        </button>
      </div>
    </div>
  )
}

export function MainLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const { user, logout, isAuthenticated } = useAuthStore()
  const { currentTheme, isBlockStatusVerified, isLoading: isThemeLoading, error: themeError, fetchTheme } = useThemeStore()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // checkAuth é gerenciado centralmente pelo AuthProvider
  }, [])

  // Dono sem plano Mentorfy ativo: a área inteira é bloqueada. Se o aluno já
  // estiver logado, deslogamos automaticamente (a tela de bloqueio segue
  // renderizada em qualquer rota, então não há para onde "redirecionar" além
  // dela própria).
  const planExpired = currentTheme?.owner_plan_expired === true
  useEffect(() => {
    if (!mounted || !planExpired || !isAuthenticated) return
    const isWhiteLabel = Boolean(getWhiteLabelDomain(window.location.hostname))
    if (isWhiteLabel) {
      logout().catch(() => {})
    }
  }, [mounted, planExpired, isAuthenticated, logout])

  if (!mounted) {
    return null
  }

  const whiteLabelDomain = getWhiteLabelDomain(window.location.hostname)
  const isWhiteLabel = Boolean(whiteLabelDomain)
  const shouldGuardMemberArea = isWhiteLabel && isMemberAreaPath(pathname)

  // Enquanto o status de bloqueio não foi verificado, segurar o conteúdo de
  // membro (mesmo comportamento de antes — evita flash de conteúdo protegido).
  if (shouldGuardMemberArea && !isBlockStatusVerified) {
    // Tema falhou (domínio inexistente, timeout, erro de rede): mostra o erro
    // com opção de tentar de novo, em vez de girar para sempre.
    if (themeError && !isThemeLoading && whiteLabelDomain) {
      return (
        <MemberAreaErrorState
          notFound={themeError === 'HTTP 404'}
          onRetry={() => fetchTheme(whiteLabelDomain)}
        />
      )
    }
    return <MemberAreaVerificationState />
  }

  // Validação pública de certificado: nunca bloqueada — um certificado já emitido
  // precisa continuar verificável mesmo com o plano do mentor vencido.
  const isCertificateValidation = pathname === '/c' || pathname.startsWith('/c/')

  // Plano do dono expirado bloqueia a área inteira (vitrine, login e conteúdo).
  if (isWhiteLabel && planExpired && !isCertificateValidation) {
    return <BlockedMemberArea reason="plan-expired" />
  }

  // Bloqueio manual de moderação: escopo de rotas de membro (inalterado).
  if (shouldGuardMemberArea && currentTheme?.member_area_blocked) {
    return <BlockedMemberArea reason="member-blocked" />
  }

  const handleLogout = async () => {
    try {
      await logout()
    } finally {
      router.push('/?force_login=1')
    }
  }

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : user?.email?.substring(0, 2).toUpperCase()

  // Se não estiver autenticado, mostrar apenas o conteúdo sem sidebar
  // if (!isAuthenticated) {
  return (
    <div className="min-h-screen">
      {children}
    </div>
  )
}
